import { useCallback, useMemo } from 'react';
import { Area, AreaChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import type { LineChartOptions } from '../types';
import {
  isDimmed,
  makeSeriesClickHandler,
  markOpacity,
  useSeriesKeyboardSelection,
} from './chartSelection';
import { CartesianLegend, ChartCartesianGrid, ChartTooltip } from './parts';
import {
  AXIS_TICK_FONT_SIZE,
  CARTESIAN_CHART_MARGIN,
  type CartesianChartCaseProps,
  categoryAxisHeight,
  CHART_SERIES_ANIMATION,
  curveTypeForLineStyle,
  formatAxisValue,
  mapScale,
  resolveCssColor,
  valueAxisProps,
  xAxisLabelProps,
} from './shared';

/** Static cursor style for an interactive chart — hoisted since it never
 * depends on props. */
const POINTER_CURSOR_STYLE = { cursor: 'pointer' } as const;

/**
 * AreaChartCase component - extracted to keep chart-type rendering logic out
 * of the switch statement in ChartRendererCore (mirrors PieChartCase above).
 * Used both for the 'area' chart type and for 'line' charts with a
 * non-'none' areaFill option.
 */
export function AreaChartCase({
  chartData,
  config,
  columnTitles,
  height,
  legendAlign,
  tooltipFormatter,
  seriesKeys,
  seriesColors,
  axisMeta,
  categoryBottomLabel,
  valueAxisTitle,
  showXAxisLabels = true,
  yAxisWidth,
  renderLegend = true,
  syncId,
  lineOptions,
  gradientPrefix,
  onDatapointSelect,
  activeSelection,
}: CartesianChartCaseProps & {
  lineOptions: LineChartOptions;
  /** Unique-per-instance id prefix for `<linearGradient>` ids — recharts
   * `<defs>` ids collide across multiple chart instances/panels sharing a
   * document without one. */
  gradientPrefix: string;
}) {
  const xAxisHeight = categoryAxisHeight({
    hasTitle: Boolean(categoryBottomLabel),
  });

  // Stable click handler factory — each Area gets its own key-scoped handler.
  const makeClickHandler = useCallback(
    (seriesKey: string) => makeSeriesClickHandler(seriesKey, onDatapointSelect),
    [onDatapointSelect]
  );

  const { handleMouseMove, handleContainerKeyDown } =
    useSeriesKeyboardSelection({ chartData, seriesKeys, onDatapointSelect });

  const interactive = Boolean(onDatapointSelect);

  const xAxisTick = useMemo(
    () =>
      showXAxisLabels
        ? {
            fill: resolveCssColor('--color-text-secondary'),
            fontSize: AXIS_TICK_FONT_SIZE,
          }
        : false,
    [showXAxisLabels]
  );

  const areaChart = (
    <AreaChart
      data={chartData}
      margin={CARTESIAN_CHART_MARGIN}
      syncId={syncId}
      style={interactive ? POINTER_CURSOR_STYLE : undefined}
      onMouseMove={interactive ? handleMouseMove : undefined}
    >
      <defs>
        {seriesKeys.map(key => (
          <linearGradient
            key={key}
            id={`${gradientPrefix}-area-${key}`}
            x1='0'
            y1='0'
            x2='0'
            y2='1'
          >
            <stop offset='0%' stopColor={seriesColors[key]} stopOpacity={0.3} />
            <stop
              offset='100%'
              stopColor={seriesColors[key]}
              stopOpacity={0.05}
            />
          </linearGradient>
        ))}
      </defs>
      <ChartCartesianGrid show={config.axes?.xAxis.showGrid} />
      <XAxis
        dataKey='x'
        hide={!config.axes?.xAxis.show}
        axisLine={false}
        tickLine={false}
        tick={xAxisTick}
        height={xAxisHeight}
        label={
          showXAxisLabels ? xAxisLabelProps(categoryBottomLabel) : undefined
        }
        angle={config.axes?.xAxis.rotation}
      />
      <YAxis
        {...valueAxisProps({
          axisMeta,
          axisConfig: config.axes?.yAxisLeft,
          label: valueAxisTitle,
          width: yAxisWidth,
          scale: mapScale(config.axes?.yAxisLeft.scale || 'linear'),
          tickFormatter: config.axes?.yAxisLeft?.unit
            ? (value: number) => formatAxisValue(value, config.axes?.yAxisLeft)
            : axisMeta.autoTickFormatter,
          ticks: axisMeta.smallIntegerTicks,
        })}
      />
      <ChartTooltip show={config.tooltip.show} formatter={tooltipFormatter} />
      <CartesianLegend
        show={config.legend.show && renderLegend}
        legend={config.legend}
        legendAlign={legendAlign}
      />
      {seriesKeys.map(key => {
        const curveType = curveTypeForLineStyle(lineOptions.lineStyle);

        let fillValue: string;
        if (lineOptions.areaFill === 'solid') {
          fillValue = seriesColors[key];
        } else if (lineOptions.areaFill === 'none') {
          fillValue = 'none';
        } else {
          fillValue = `url(#${gradientPrefix}-area-${key})`;
        }

        let fillOpacityValue: number;
        if (lineOptions.areaFill === 'solid') {
          fillOpacityValue = 0.2;
        } else if (lineOptions.areaFill === 'none') {
          fillOpacityValue = 0;
        } else {
          fillOpacityValue = 1;
        }

        // Per-point dimming on an Area is impractical (recharts Area does not
        // accept Cell children). We dim at the series level instead: if
        // another series is active, this series' stroke/fill goes to 0.35.
        const seriesDimmed =
          activeSelection !== null &&
          activeSelection !== undefined &&
          isDimmed(activeSelection, activeSelection.rowIndex, key);
        const opacity = markOpacity(seriesDimmed);

        return (
          <Area
            key={key}
            type={curveType}
            dataKey={key}
            name={columnTitles[key] || key}
            stroke={seriesColors[key]}
            strokeOpacity={opacity}
            strokeWidth={2}
            fill={fillValue}
            fillOpacity={fillOpacityValue * opacity}
            dot={
              lineOptions.showPoints
                ? {
                    fill: seriesColors[key],
                    strokeWidth: 2,
                    r: 4,
                  }
                : false
            }
            activeDot={{
              fill: 'var(--color-bg-primary)',
              stroke: seriesColors[key],
              strokeWidth: 2,
              r: 5,
            }}
            onClick={makeClickHandler(key)}
            {...CHART_SERIES_ANIMATION}
          />
        );
      })}
    </AreaChart>
  );

  if (interactive) {
    return (
      <div
        tabIndex={0}
        onKeyDown={handleContainerKeyDown}
        className='outline-none'
        aria-label='Interactive area chart — press Enter or Space to drill into the hovered point, or the first point if none has been hovered'
      >
        <ResponsiveContainer width='100%' height={height}>
          {areaChart}
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <ResponsiveContainer width='100%' height={height}>
      {areaChart}
    </ResponsiveContainer>
  );
}
